import { Injectable, Inject, Optional, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Observable, fromEvent } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { filter, map } from 'rxjs/operators';

export interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export interface PWAInstallInfo {
  canInstall: boolean;
  isInstalled: boolean;
  isStandalone: boolean;
  platform: 'ios' | 'android' | 'desktop' | 'unknown';
}

@Injectable({
  providedIn: 'root',
})
export class PWAService {
  private installPromptEvent: InstallPromptEvent | null = null;
  private installInfoSubject = new BehaviorSubject<PWAInstallInfo>({
    canInstall: false,
    isInstalled: false,
    isStandalone: false,
    platform: 'unknown',
  });

  public installInfo$ = this.installInfoSubject.asObservable();
  public updateAvailable$ = new BehaviorSubject<boolean>(false);
  public isOnline$ = new BehaviorSubject<boolean>(navigator.onLine);

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    if (isPlatformBrowser(this.platformId)) {
      this.initializePWA();
      this.setupUpdateListener();
      this.setupOnlineListener();
    }
  }

  private initializePWA(): void {
    // Detectar plataforma
    const platform = this.detectPlatform();

    // Verificar se está em modo standalone
    const isStandalone = this.isStandaloneMode();

    // Verificar se já está instalado
    const isInstalled = this.isAppInstalled();

    // Listener para evento de instalação
    window.addEventListener('beforeinstallprompt', (e: Event) => {
      e.preventDefault();
      this.installPromptEvent = e as InstallPromptEvent;

      this.updateInstallInfo({
        canInstall: true,
        isInstalled,
        isStandalone,
        platform,
      });
    });

    // Listener para quando o app é instalado
    window.addEventListener('appinstalled', () => {
      this.installPromptEvent = null;
      this.updateInstallInfo({
        canInstall: false,
        isInstalled: true,
        isStandalone: true,
        platform,
      });

      this.showInstallSuccessNotification();
    });

    // Estado inicial
    this.updateInstallInfo({
      canInstall: false,
      isInstalled,
      isStandalone,
      platform,
    });
  }

  private detectPlatform(): 'ios' | 'android' | 'desktop' | 'unknown' {
    const userAgent = window.navigator.userAgent.toLowerCase();

    if (/iphone|ipad|ipod/.test(userAgent)) {
      return 'ios';
    } else if (/android/.test(userAgent)) {
      return 'android';
    } else if (/win|mac|linux/.test(userAgent)) {
      return 'desktop';
    }

    return 'unknown';
  }

  private isStandaloneMode(): boolean {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone ||
      document.referrer.includes('android-app://')
    );
  }

  private isAppInstalled(): boolean {
    return (
      this.isStandaloneMode() ||
      localStorage.getItem('pwa-installed') === 'true'
    );
  }

  private updateInstallInfo(info: PWAInstallInfo): void {
    this.installInfoSubject.next(info);
  }

  private setupUpdateListener(): void {
    // Service Worker update detection (if available)
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        this.updateAvailable$.next(true);
      });
    }
  }

  private setupOnlineListener(): void {
    fromEvent(window, 'online').subscribe(() => {
      this.isOnline$.next(true);
    });

    fromEvent(window, 'offline').subscribe(() => {
      this.isOnline$.next(false);
    });
  }

  // Métodos públicos
  public async installApp(): Promise<boolean> {
    if (!this.installPromptEvent) {
      return false;
    }

    try {
      await this.installPromptEvent.prompt();
      const choiceResult = await this.installPromptEvent.userChoice;

      if (choiceResult.outcome === 'accepted') {
        localStorage.setItem('pwa-installed', 'true');
        this.installPromptEvent = null;
        return true;
      }

      return false;
    } catch (error) {
      console.error('Erro ao instalar PWA:', error);
      return false;
    }
  }

  public async updateApp(): Promise<void> {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.ready;
        if (registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
        window.location.reload();
      } catch (error) {
        console.error('Erro ao atualizar app:', error);
      }
    }
  }

  public getInstallInstructions(): string {
    const platform = this.installInfoSubject.value.platform;

    switch (platform) {
      case 'ios':
        return 'Toque no botão de compartilhar e selecione "Adicionar à Tela Inicial"';
      case 'android':
        return 'Toque no menu (⋮) e selecione "Instalar app" ou "Adicionar à tela inicial"';
      case 'desktop':
        return 'Clique no ícone de instalação na barra de endereços ou use Ctrl+Shift+A';
      default:
        return 'Use o menu do seu navegador para instalar este app';
    }
  }

  public showInstallBanner(): void {
    // Este método será chamado pelo componente de instalação
    console.log('📱 Mostrando banner de instalação PWA');
  }

  private showInstallSuccessNotification(): void {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('Estokai instalado com sucesso!', {
        body: 'O app agora está disponível na sua tela inicial',
        icon: '/assets/icons/icon-192x192.png',
        badge: '/assets/icons/icon-72x72.png',
      });
    }
  }

  // Recursos inovadores
  public async shareContent(data: {
    title: string;
    text: string;
    url?: string;
    files?: File[];
  }): Promise<boolean> {
    if (navigator.share) {
      try {
        await navigator.share(data);
        return true;
      } catch (error) {
        console.error('Erro ao compartilhar:', error);
        return false;
      }
    }

    // Fallback: copiar para clipboard
    if (navigator.clipboard && data.url) {
      try {
        await navigator.clipboard.writeText(
          `${data.title}\n${data.text}\n${data.url}`
        );
        return true;
      } catch (error) {
        console.error('Erro ao copiar para clipboard:', error);
        return false;
      }
    }

    return false;
  }

  public async requestNotificationPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission === 'denied') {
      return false;
    }

    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  public async showNotification(
    title: string,
    options?: NotificationOptions
  ): Promise<void> {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      const registration = await navigator.serviceWorker.ready;

      // Vibrate if supported
      if ('vibrate' in navigator) {
        navigator.vibrate([200, 100, 200]);
      }

      await registration.showNotification(title, {
        icon: '/assets/icons/icon-192x192.png',
        badge: '/assets/icons/icon-72x72.png',
        data: { timestamp: Date.now() },
        ...options,
      });
    }
  }

  public getDeviceInfo(): {
    isMobile: boolean;
    isTablet: boolean;
    isDesktop: boolean;
    hasCamera: boolean;
    hasGeolocation: boolean;
    orientation: string;
  } {
    const isMobile = /Mobi|Android/i.test(navigator.userAgent);
    const isTablet = /Tablet|iPad/i.test(navigator.userAgent);
    const isDesktop = !isMobile && !isTablet;

    return {
      isMobile,
      isTablet,
      isDesktop,
      hasCamera:
        'mediaDevices' in navigator && 'getUserMedia' in navigator.mediaDevices,
      hasGeolocation: 'geolocation' in navigator,
      orientation: screen.orientation?.type || 'unknown',
    };
  }

  /**
   * Check if Web Share API is available
   */
  canShare(): boolean {
    return 'share' in navigator;
  }

  /**
   * Share content using Web Share API
   */
  async share(shareData: ShareData): Promise<boolean> {
    try {
      if (this.canShare()) {
        await navigator.share(shareData);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Share error:', error);
      return false;
    }
  }
}
